import { useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { setSelectedCenter } from '../../../services/ConfigurationService';
import { refundApi } from '../../../services/RefundService';
import { writeoffApi } from '../../../services/WriteoffService';
import { registrationApi } from '../../../services/RegistrationService';
import { caseManagementApi } from '../../../services/CaseManagementService';
import { encounterApi } from '../../../services/EncounterService';
import { accountApi } from '../../../services/AccountService';
import { medNecApi } from '../../../services/MedNecService';
import { laborManagementApi } from '../../../services/LaborManagementService';

const CENTER_AWARE_PATHS = [
  '/refunds',
  '/writeoffs',
  '/schedule',
  '/PatientLookup',
  '/registration',
  '/case-management',
  '/encounters',
  '/contracts',
  '/mednec',
  '/labor-management',
  '/medical-records',
  '/surgery-board',
];

export function useCenterSelection() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const handleCenterChange = useCallback((code: string) => {
    dispatch(setSelectedCenter(code));

    // Invalidate center-specific caches across all modules
    dispatch(refundApi.util.invalidateTags(['RefundList', 'RefundWorklist', 'Refund']));
    dispatch(writeoffApi.util.invalidateTags(['Writeoff', 'WriteoffWorklist']));
    dispatch(registrationApi.util.invalidateTags(['Registration', 'Registrations']));
    dispatch(caseManagementApi.util.invalidateTags(['Worklist', 'CaseManagement']));
    dispatch(encounterApi.util.invalidateTags(['Encounter', 'Encounters']));
    dispatch(accountApi.util.invalidateTags(['Account', 'AccountList', 'WriteOffCandidates']));
    dispatch(medNecApi.util.invalidateTags(['MedNecCaseList', 'MedNecDashboard']));
    dispatch(laborManagementApi.util.invalidateTags(['StaffingRule', 'BudgetSummary']));

    // Update URL params if on a center-aware page
    const isCenterAwarePage = CENTER_AWARE_PATHS.some(path =>
      location.pathname.startsWith(path)
    );

    if (isCenterAwarePage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('center', code);
      setSearchParams(newParams, { replace: true });
    }
  }, [dispatch, searchParams, setSearchParams, location.pathname]);

  return { handleCenterChange };
}
